import { getCurrentUserPreferences, updateCurrentUserPreferences } from 'api/userPreferences';
import { useEffect, useRef } from 'react';
import { useAppSelector } from 'redux/hooks';
import useLocalStorage from 'use-local-storage';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';

import {
  buildCasesViewModePreferencesPatch,
  CASES_VIEW_MODE_STORAGE_KEY,
  CasesViewMode,
  getCasesViewModeFromUserPreferences,
  hasStoredPreferenceValue,
} from 'utils/userPreferences';

const DEFAULT_CASES_VIEW_MODE: CasesViewMode = 'card';

export const useCasesViewMode = () => {
  const [userSelectedProcedureViewMode, setUserSelectedProcedureViewMode] = useLocalStorage<CasesViewMode>(
    CASES_VIEW_MODE_STORAGE_KEY,
    DEFAULT_CASES_VIEW_MODE
  );
  const [casesViewModeParam, setCasesViewModeQueryParamParam] = useQueryParam(
    'casesViewMode',
    withDefault(StringParam, userSelectedProcedureViewMode),
    { updateType: 'replaceIn' }
  );
  const { accessToken, triedAuthenticateFromLocalStorage } = useAppSelector((state) => state.auth);
  const loadedAccessTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!triedAuthenticateFromLocalStorage) {
      return;
    }

    if (!accessToken) {
      loadedAccessTokenRef.current = null;
      return;
    }

    if (loadedAccessTokenRef.current === accessToken) {
      return;
    }

    loadedAccessTokenRef.current = accessToken;

    let ignoreResponse = false;

    void getCurrentUserPreferences()
      .then((preferences) => {
        if (ignoreResponse) {
          return;
        }

        const serverCasesViewMode = getCasesViewModeFromUserPreferences(preferences ?? {});
        if (serverCasesViewMode) {
          setUserSelectedProcedureViewMode(serverCasesViewMode);
          return;
        }

        if (hasStoredPreferenceValue(CASES_VIEW_MODE_STORAGE_KEY)) {
          void updateCurrentUserPreferences(buildCasesViewModePreferencesPatch(userSelectedProcedureViewMode)).catch(
            () => undefined
          );
        }
      })
      .catch(() => undefined);

    return () => {
      ignoreResponse = true;
    };
  }, [accessToken, setUserSelectedProcedureViewMode, triedAuthenticateFromLocalStorage, userSelectedProcedureViewMode]);

  return {
    casesViewMode: (casesViewModeParam || userSelectedProcedureViewMode || DEFAULT_CASES_VIEW_MODE) as CasesViewMode,
    setCasesViewMode: (casesViewMode: CasesViewMode) => {
      const validCasesViewMode = casesViewMode || userSelectedProcedureViewMode || DEFAULT_CASES_VIEW_MODE;
      setUserSelectedProcedureViewMode(validCasesViewMode);
      setCasesViewModeQueryParamParam(validCasesViewMode);

      if (accessToken) {
        void updateCurrentUserPreferences(buildCasesViewModePreferencesPatch(validCasesViewMode)).catch(
          () => undefined
        );
      }
    },
    defaultViewMode: userSelectedProcedureViewMode || DEFAULT_CASES_VIEW_MODE,
  };
};
